import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import { SettingsRow } from '@/components/profile/SettingsRow';
import { SettingsGroup } from '@/components/profile/SettingsGroup';
import { Switch } from '@/components/profile/Switch';
import type { SupportedLocale } from '@/i18n';
import '@/styles/profile.css';

const LANG_LABEL: Record<SupportedLocale, string> = {
  zh: '简体中文',
  'zh-TW': '繁體中文',
  en: 'English',
  vi: 'Tiếng Việt',
  km: 'ខ្មែរ',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

export function ProfileRoute() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);

  if (!user) {
    return <div className="profile-page" aria-busy="true" />;
  }

  async function onSignOut() {
    if (!window.confirm(t('profile.confirm_sign_out'))) return;
    await signOut();
    toast.success(t('profile.signed_out'));
    navigate('/login', { replace: true });
  }

  const currentLang = (Object.keys(LANG_LABEL) as SupportedLocale[]).includes(
    i18n.language as SupportedLocale,
  )
    ? LANG_LABEL[i18n.language as SupportedLocale]
    : LANG_LABEL.en;

  return (
    <div className="profile-page">
      <section className="profile-hero">
        <div className="profile-hero-avatar">{initials(user.displayName)}</div>
        <div className="profile-hero-info">
          <div className="profile-hero-name">{user.displayName}</div>
          <div className="profile-hero-username">@{user.username}</div>
        </div>
      </section>

      <SettingsGroup title={t('profile.section_account')}>
        <SettingsRow
          icon="person"
          label={t('profile.row_account')}
          to="/profile/account"
        />
      </SettingsGroup>

      <SettingsGroup title={t('profile.section_preferences')}>
        <SettingsRow
          icon="translate"
          label={t('profile.row_language')}
          value={currentLang}
          to="/profile/language"
        />
        <SettingsRow
          icon={dark ? 'dark_mode' : 'light_mode'}
          label={t('profile.row_theme_dark')}
          trailing={
            <Switch
              on={dark}
              onChange={() => setDark((d) => !d)}
              ariaLabel={t('profile.row_theme_dark')}
            />
          }
          onClick={() => setDark((d) => !d)}
        />
      </SettingsGroup>

      <SettingsGroup title={t('profile.section_security')}>
        <SettingsRow
          icon="lock"
          label={t('profile.row_password')}
          to="/profile/security"
        />
        <SettingsRow
          icon="devices"
          label={t('profile.row_devices')}
          to="/profile/devices"
        />
      </SettingsGroup>

      <SettingsGroup>
        <SettingsRow
          icon="logout"
          label={t('profile.sign_out')}
          onClick={onSignOut}
          destructive
        />
      </SettingsGroup>

      <div className="profile-version">{t('login.version')}</div>
    </div>
  );
}
